import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, X, Shield, Wrench, Calendar, Search, Lock, User as UserIcon, Users } from 'lucide-react';
import type { Technician, User as UserType, UserPermission } from '@shared/types';
import { getApiUrl } from '@/config';

interface UserManagerProps {
    technicians: Technician[];
}

const DEFAULT_PERMISSIONS: UserPermission = {
    planning: 'none',
    fleet: 'none',
    tooling: 'none'
};

export const UserManager: React.FC<UserManagerProps> = ({ technicians }) => {
    const [users, setUsers] = useState<UserType[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [activeTab, setActiveTab] = useState<'active' | 'archived'>('active');

    // Form State
    const [editingUser, setEditingUser] = useState<UserType | null>(null);
    const [formData, setFormData] = useState<{
        username: string;
        password: string;
        role: 'admin' | 'technician' | 'planner' | 'user' | 'magasinier';
        technicianId: string;
        permissions: UserPermission;
        createLink: boolean; // New checkbox state
    }>({
        username: '',
        password: '',
        role: 'technician',
        technicianId: '',
        permissions: { ...DEFAULT_PERMISSIONS },
        createLink: true // Default to true for better UX
    });

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Keep formData clean when editing
    useEffect(() => {
        if (editingUser) {
            setFormData(prev => ({ ...prev, createLink: false }));
        }
    }, [editingUser]);

    useEffect(() => {
        fetchUsers();
    }, []);

    const getRandomColor = () => {
        const colors = [
            '#ef4444', '#f97316', '#f59e0b', '#84cc16', '#10b981', '#06b6d4',
            '#3b82f6', '#6366f1', '#8b5cf6', '#d946ef', '#f43f5e'
        ];
        return colors[Math.floor(Math.random() * colors.length)];
    };

    const fetchUsers = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(getApiUrl('/api/users'), {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (res.ok) {
                const data = await res.json();
                setUsers(data);
            } else {
                throw new Error('Failed to fetch');
            }
        } catch (e) {
            console.error('Failed to fetch users', e);
            setError('Erreur de connexion au serveur');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            let finalTechId = formData.technicianId;

            // 1. Auto-Create Technician Profile logic
            if (!editingUser && formData.createLink && (formData.role === 'technician' || formData.role === 'user')) {
                const newTechId = `tech-${Date.now()}`;
                const newTech = {
                    id: newTechId,
                    name: formData.username // Use username as name
                        .split('.')
                        .map(n => n.charAt(0).toUpperCase() + n.slice(1)) // Capitalize (thomas.dupont -> Thomas Dupont)
                        .join(' '),
                    service: 'Maintenance', // Default
                    color: getRandomColor(),
                    status: 'active',
                    assignments: []
                };

                const token = localStorage.getItem('token');

                // Note: The API expects an array of technicians to update
                const techRes = await fetch(getApiUrl('/api/technicians'), {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify([newTech])
                });

                if (techRes.ok) {
                    finalTechId = newTechId;
                    // Optimistic update of local technicians list if needed (prop callback?) 
                    // But here we rely on the fact that User creation is the goal.
                } else {
                    alert('Erreur lors de la création automatique du technicien. Le compte utilisateur ne sera pas lié.');
                }
            }

            // 2. Create/Update User
            const url = editingUser
                ? getApiUrl(`/api/users/${editingUser.id}`)
                : getApiUrl('/api/users');

            const method = editingUser ? 'PUT' : 'POST';

            // Only send password if it's not empty (for updates)
            const body = {
                ...formData,
                technicianId: finalTechId // Use the computed ID
            };

            // Clean up internal flags
            delete (body as any).createLink;

            if (editingUser && !body.password) {
                delete (body as any).password;
            }

            const token = localStorage.getItem('token');
            const res = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(body)
            });

            if (res.ok) {
                setShowModal(false);
                setEditingUser(null);
                setFormData({
                    username: '',
                    password: '',
                    role: 'magasinier',
                    technicianId: '',
                    permissions: { ...DEFAULT_PERMISSIONS },
                    createLink: true
                });
                fetchUsers();
                // We should also trigger a refresh of technicians in the parent or context, 
                // but usually the socket or next reload handles it.
                // A reload of the page might be safest to see the new technician in lists immediately if we don't have context access.
                // For now, let's assume Sidebar/App handles data refresh via Sockets.
            } else {
                alert('Erreur lors de la sauvegarde de l\'utilisateur');
            }
        } catch (e) {
            console.error('Error saving user', e);
        }
    };

    const handleEdit = (user: UserType) => {
        setEditingUser(user);
        setFormData({
            username: user.username,
            password: '', // Don't show existing password
            role: user.role,
            technicianId: user.technicianId || '',
            permissions: user.permissions || { ...DEFAULT_PERMISSIONS },
            createLink: false
        });
        setShowModal(true);
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) return;

        try {
            const token = localStorage.getItem('token');
            await fetch(getApiUrl(`/api/users/${id}`), {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            fetchUsers();
        } catch (e) {
            console.error('Error deleting user', e);
        }
    };

    const filteredUsers = users.filter(u => {
        const matchesSearch = u.username.toLowerCase().includes(searchTerm.toLowerCase());
        const isArchived = u.archived === true;
        if (activeTab === 'active') return matchesSearch && !isArchived;
        return matchesSearch && isArchived;
    });

    const toggleArchive = async (user: UserType) => {
        const action = user.archived ? 'désarchiver' : 'archiver';
        if (!confirm(`Êtes-vous sûr de vouloir ${action} cet utilisateur ?`)) return;

        try {
            const token = localStorage.getItem('token');
            const res = await fetch(getApiUrl(`/api/users/${user.id}`), {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    ...user,
                    archived: !user.archived
                })
            });

            if (res.ok) {
                fetchUsers();
            } else {
                alert('Erreur lors de la modification du statut');
            }
        } catch (e) {
            console.error('Error toggling archive', e);
        }
    };



    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                        type="text"
                        placeholder="Rechercher un utilisateur..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="glass-input pl-10 w-full"
                    />
                </div>
                <button
                    onClick={() => {
                        setEditingUser(null);
                        setFormData({
                            username: '',
                            password: '',
                            role: 'user',
                            technicianId: '',
                            permissions: { ...DEFAULT_PERMISSIONS },
                            createLink: true
                        });
                        setShowModal(true);
                    }}
                    className="glass-button bg-primary/20 text-primary border-primary/50 flex items-center gap-2"
                >
                    <Plus size={18} />
                    Nouvel Utilisateur
                </button>
            </div>

            {/* Status Tabs */}
            <div className="flex gap-4 border-b border-white/5 mb-6">
                <button
                    onClick={() => setActiveTab('active')}
                    className={`pb-3 px-2 text-sm font-medium transition-colors relative ${activeTab === 'active' ? 'text-primary' : 'text-gray-500 hover:text-gray-300'}`}
                >
                    Actifs ({users.filter(u => !u.archived).length})
                    {activeTab === 'active' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t-full" />}
                </button>
                <button
                    onClick={() => setActiveTab('archived')}
                    className={`pb-3 px-2 text-sm font-medium transition-colors relative ${activeTab === 'archived' ? 'text-amber-500' : 'text-gray-500 hover:text-gray-300'}`}
                >
                    Archivés ({users.filter(u => u.archived).length})
                    {activeTab === 'archived' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-amber-500 rounded-t-full" />}
                </button>
            </div>

            <div className="glass-panel overflow-hidden min-h-[300px] relative">
                {isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20 backdrop-blur-sm z-10">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                    </div>
                )}

                {error && (
                    <div className="p-4 bg-red-500/10 border-b border-red-500/20 text-red-400 text-center">
                        {error}
                        <button onClick={fetchUsers} className="ml-4 underline hover:text-red-300">Réessayer</button>
                    </div>
                )}

                <table className="w-full text-left">
                    <thead className="bg-white/5 text-gray-400 text-sm">
                        <tr>
                            <th className="p-4">Utilisateur</th>
                            <th className="p-4">Rôle</th>
                            <th className="p-4">Lien Technicien</th>
                            <th className="p-4">Accès Modules</th>
                            <th className="p-4 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                        {filteredUsers.map(user => {
                            const linkedTech = technicians.find(t => t.id === user.technicianId);
                            const perms = user.permissions || DEFAULT_PERMISSIONS;

                            return (
                                <tr key={user.id} className="hover:bg-white/5">
                                    <td className="p-4 font-medium text-gray-200">{user.username}</td>
                                    <td className="p-4">
                                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${user.role === 'admin'
                                            ? 'bg-red-500/10 text-red-400 border-red-500/20'
                                            : user.role === 'planner'
                                                ? 'bg-purple-500/10 text-purple-400 border-purple-500/20'
                                                : user.role === 'technician'
                                                    ? 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                                                    : user.role === 'magasinier'
                                                        ? 'bg-orange-500/10 text-orange-400 border-orange-500/20'
                                                        : 'bg-gray-500/10 text-gray-400 border-gray-500/20'
                                            }`}>
                                            {user.role === 'admin' && <Shield size={12} />}
                                            {user.role === 'planner' && <Calendar size={12} />}
                                            {user.role === 'technician' && <Wrench size={12} />}
                                            {user.role === 'user' && <UserIcon size={12} />}
                                            {user.role === 'magasinier' && <Users size={12} />}
                                            {user.role === 'admin' ? 'Super Admin' :
                                                user.role === 'planner' ? 'Planificateur' :
                                                    user.role === 'technician' ? 'Technicien' :
                                                        user.role === 'magasinier' ? 'Magasinier' : 'Utilisateur'}
                                        </span>
                                    </td>
                                    <td className="p-4 text-gray-400">
                                        {linkedTech ? (
                                            <div className="flex items-center gap-2 text-white">
                                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: linkedTech.color }} />
                                                {linkedTech.name}
                                            </div>
                                        ) : (
                                            <span className="text-gray-600 italic text-xs">Non lié</span>
                                        )}
                                    </td>
                                    <td className="p-4">
                                        <div className="flex gap-2 text-xs">
                                            {perms.planning !== 'none' && (
                                                <span className={`px-2 py-0.5 rounded border ${perms.planning === 'write' ? 'bg-green-500/10 border-green-500/30 text-green-400' : 'bg-blue-500/10 border-blue-500/30 text-blue-400'}`}>Planning</span>
                                            )}
                                            {perms.fleet !== 'none' && (
                                                <span className={`px-2 py-0.5 rounded border ${perms.fleet === 'write' ? 'bg-green-500/10 border-green-500/30 text-green-400' : 'bg-blue-500/10 border-blue-500/30 text-blue-400'}`}>Flotte</span>
                                            )}
                                            {perms.tooling !== 'none' && (
                                                <span className={`px-2 py-0.5 rounded border ${perms.tooling === 'write' ? 'bg-green-500/10 border-green-500/30 text-green-400' : 'bg-blue-500/10 border-blue-500/30 text-blue-400'}`}>Outils</span>
                                            )}
                                            {Object.values(perms).every(v => v === 'none') && user.role !== 'admin' && (
                                                <span className="text-gray-600 italic">Aucun accès</span>
                                            )}
                                            {user.role === 'admin' && (
                                                <span className="text-gray-500 italic">Accès complet</span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="p-4 text-right space-x-2">
                                        <button
                                            onClick={() => handleEdit(user)}
                                            className="p-1.5 hover:bg-white/10 rounded-lg text-blue-400 transition-colors"
                                            title="Modifier"
                                        >
                                            <Edit2 size={16} />
                                        </button>
                                        <button
                                            onClick={() => toggleArchive(user)}
                                            className={`p-1.5 hover:bg-white/10 rounded-lg transition-colors ${user.archived ? 'text-green-400' : 'text-amber-400'}`}
                                            title={user.archived ? "Réactiver" : "Archiver"}
                                        >
                                            {user.archived ? <Plus size={16} /> : <X size={16} />}
                                        </button>
                                        <button
                                            onClick={() => handleDelete(user.id)}
                                            className="p-1.5 hover:bg-white/10 rounded-lg text-red-400 transition-colors"
                                            title="Supprimer définitivement"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="bg-[#0f172a] border border-white/10 rounded-xl w-full max-w-lg shadow-2xl overflow-y-auto max-h-[90vh] p-6 space-y-6">
                        <div className="flex justify-between items-center">
                            <h3 className="text-xl font-bold text-gray-100">
                                {editingUser ? 'Modifier Utilisateur' : 'Nouvel Utilisateur'}
                            </h3>
                            <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white">
                                <X size={24} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-400 mb-1">Nom d'utilisateur / Login</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.username}
                                        onChange={e => setFormData({ ...formData, username: e.target.value })}
                                        className="glass-input w-full"
                                        placeholder="ex: thomas.maintenance"
                                    />
                                </div>

                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-400 mb-1">
                                        {editingUser ? 'Nouveau Mot de passe (optionnel)' : 'Mot de passe'}
                                    </label>
                                    <input
                                        type="password"
                                        required={!editingUser}
                                        value={formData.password}
                                        onChange={e => setFormData({ ...formData, password: e.target.value })}
                                        className="glass-input w-full"
                                        placeholder="••••••••"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-1">Rôle Principal</label>
                                    <select
                                        value={formData.role}
                                        onChange={e => setFormData({ ...formData, role: e.target.value as any })}
                                        className="glass-input w-full"
                                    >
                                        <option value="user">Utilisateur Standard</option>
                                        <option value="technician">Technicien</option>
                                        <option value="magasinier">Magasinier</option>
                                        <option value="planner">Planificateur</option>
                                        <option value="admin">Administrateur</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-400 mb-1">Lier au Technicien</label>

                                    {/* Auto-Creation Logic UI */}
                                    {!editingUser && (formData.role === 'technician' || formData.role === 'user') && (
                                        <div className="mb-2">
                                            <label className="flex items-center gap-2 cursor-pointer p-2 rounded bg-blue-500/10 border border-blue-500/20 text-blue-300 text-xs">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.createLink}
                                                    onChange={e => setFormData({ ...formData, createLink: e.target.checked })}
                                                    className="rounded border-blue-500/50 bg-blue-500/20"
                                                />
                                                <span>Créer automatiquement la fiche technicien</span>
                                            </label>
                                        </div>
                                    )}

                                    <select
                                        value={formData.technicianId}
                                        onChange={e => setFormData({ ...formData, technicianId: e.target.value })}
                                        className="glass-input w-full"
                                        disabled={formData.createLink && !editingUser && (formData.role === 'technician' || formData.role === 'user')}
                                    >
                                        <option value="">{formData.createLink && !editingUser && (formData.role === 'technician' || formData.role === 'user') ? '-- Sera créé automatiquement --' : '-- Aucun lien --'}</option>
                                        {technicians.map(t => (
                                            <option key={t.id} value={t.id}>{t.name} ({t.service})</option>
                                        ))}
                                    </select>
                                    <p className="text-[10px] text-gray-500 mt-1">Requis pour apparaître sur le planning.</p>
                                </div>
                            </div>


                            {/* Permissions Module */}
                            <div className="bg-white/5 p-4 rounded-lg border border-white/10 space-y-3">
                                <div className="flex items-center gap-2 mb-2">
                                    <Lock size={16} className="text-primary" />
                                    <h4 className="font-semibold text-gray-200">Permissions Granulaires</h4>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-400 mb-1">Planning</label>
                                        <select
                                            value={formData.permissions.planning}
                                            onChange={e => setFormData({
                                                ...formData,
                                                permissions: { ...formData.permissions, planning: e.target.value as any }
                                            })}
                                            className="glass-input w-full text-sm py-1"
                                        >
                                            <option value="none">Aucun accès</option>
                                            <option value="read">Consultation</option>
                                            <option value="write">Modification</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-400 mb-1">Flotte Véhicules</label>
                                        <select
                                            value={formData.permissions.fleet}
                                            onChange={e => setFormData({
                                                ...formData,
                                                permissions: { ...formData.permissions, fleet: e.target.value as any }
                                            })}
                                            className="glass-input w-full text-sm py-1"
                                        >
                                            <option value="none">Aucun accès</option>
                                            <option value="read">Consultation</option>
                                            <option value="write">Modification</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-400 mb-1">Outillage</label>
                                        <select
                                            value={formData.permissions.tooling}
                                            onChange={e => setFormData({
                                                ...formData,
                                                permissions: { ...formData.permissions, tooling: e.target.value as any }
                                            })}
                                            className="glass-input w-full text-sm py-1"
                                        >
                                            <option value="none">Aucun accès</option>
                                            <option value="read">Consultation</option>
                                            <option value="write">Modification</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="px-4 py-2 rounded-lg text-gray-400 hover:bg-white/5 transition-colors"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    className="glass-button bg-primary/20 text-primary border-primary/50"
                                >
                                    Enregistrer
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};
